import {
  useTheme,
  Box,
  Typography,
  Button,
  Backdrop,
  Collapse,
} from "@mui/material";
import { tokens } from "./theme/tokens";
import { NavLink } from "react-router-dom";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import menuItemList from "./menuItemList";

const MobileMenu = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const [open, setOpen] = useState(false);
  const rowRef = useRef(null);
  const [colMax, setColMax] = useState(null);

  function handleClose() {
    setOpen(false);
  }

  // The menu expands upward from the bottom bar, so the row's BOTTOM stays anchored near the
  // screen bottom while its top grows up — making the bottom a stable thing to measure (the
  // top would feed back into the height we set). Cap each category column so the whole menu
  // fits between the top of the screen and that bottom anchor; overflow wraps into a new
  // column. One shared measurement avoids per-group inconsistency from stretched rows.
  useEffect(() => {
    if (!open) return;

    const measure = () => {
      if (!rowRef.current) return;
      const { bottom } = rowRef.current.getBoundingClientRect();
      // Reserve everything that must stay visible above the column: the 35px toggle button
      // (pinned above the menu content), the app topbar, the category title + paddings, and
      // a little safe-area at the very top. Too small and the menu grows tall enough to push
      // the toggle button off the top of the screen.
      const reserve = 170;
      setColMax((prev) => {
        const next = Math.max(120, Math.floor(bottom - reserve));
        return prev === next ? prev : next;
      });
    };

    // Measure now and again after the Collapse expand animation (~300ms) settles.
    const raf = requestAnimationFrame(measure);
    const timeout = setTimeout(measure, 320);
    window.visualViewport?.addEventListener("resize", measure);
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
      window.visualViewport?.removeEventListener("resize", measure);
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [open]);

  return (
    <>
      <Box
        className="sm:hidden relative z-50  overflow-visible w-full"
        sx={{
          position: "absolute",
          transition: "transform 0.3s",
        }}
      >
        <Button
          onClick={() => setOpen(!open)}
          sx={{
            position: "absolute",
            borderRadius: "10px 10px 0 0",
            top: "-35px",
            height: "35px",
            backgroundColor: colors.card,
            ":hover": {
              backgroundColor: colors.card,
            },
          }}
          className="z-50 w-full backdrop-blur-lg"
          variant="contained"
        >
          <ArrowDropUpIcon
            className={open ? "rotate-180" : ""}
            sx={{
              transition: "transform 0.3s",
            }}
          />
        </Button>
        <Collapse in={open}>
          <Box
            ref={rowRef}
            className="w-full flex flex-row gap-2 z-40 overflow-x-auto pt-2 relative border-t-2 pb-4"
            sx={{
              borderColor: colors.info,
            }}
          >
            <Backdrop open={open} onClick={handleClose}></Backdrop>
            {menuItemList().map((group) => (
              <MenuGroup
                key={group.title}
                group={group}
                onClose={handleClose}
                colMax={colMax}
              />
            ))}
          </Box>
        </Collapse>
      </Box>
    </>
  );
};

const ITEM_GAP = 8; // gap-2 between items, in px

const MenuGroup = ({ group, onClose, colMax }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const firstColRef = useRef(null);
  const [itemHeight, setItemHeight] = useState(null);

  const visibleItems = group.items.filter((item) => item.hidden === false);

  // Measure a single item's height from the first rendered column. CSS column flex-wrap with
  // max-height is unreliable, so instead we compute how many items fit per column and split
  // them explicitly — deterministic, so it always wraps and never clips.
  useLayoutEffect(() => {
    const col = firstColRef.current;
    if (!col || !col.children.length) return;
    const count = col.children.length;
    const total = col.getBoundingClientRect().height;
    const h = (total - (count - 1) * ITEM_GAP) / count;
    if (h > 0) {
      setItemHeight((prev) => (prev !== null && Math.abs(prev - h) < 1 ? prev : h));
    }
  }, [visibleItems.length]);

  if (visibleItems.length === 0) return;

  // How many items fit in one column for the measured available height. Until we know both
  // the available height and the item height, render everything in a single column (which is
  // also what the measurement pass needs).
  const perColumn =
    colMax && itemHeight
      ? Math.max(1, Math.floor((colMax + ITEM_GAP) / (itemHeight + ITEM_GAP)))
      : visibleItems.length;

  const columns = [];
  for (let i = 0; i < visibleItems.length; i += perColumn) {
    columns.push(visibleItems.slice(i, i + perColumn));
  }

  return (
    <Box
      className="relative flex flex-col backdrop-blur-lg rounded-lg border-[1px]"
      sx={{ backgroundColor: colors.glass, borderColor: colors.info + "30" }}
    >
      <Typography
        className="py-2 border-b-2"
        textAlign="center"
        variant="h6"
        color={colors.grey[800]}
        borderColor={colors.info}
      >
        {group.title}
      </Typography>
      <Box className="flex flex-row gap-2 pt-2 items-start">
        {columns.map((column, columnIndex) => (
          <Box
            key={columnIndex}
            ref={columnIndex === 0 ? firstColRef : undefined}
            className="flex flex-col gap-2"
          >
            {column.map((item) => (
              <MenuItem
                key={group.title + "-" + item.title}
                onSelect={onClose}
                title={item.title}
                to={item.to}
                icon={item.icon}
                hidden={item.hidden}
                newTab={item.newTab}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const MenuItem = ({ title, to, icon, onSelect, hidden, newTab }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  if (hidden) return;

  const content = (
      <Box
        className="flex flex-row justify-center w-[170px] items-center gap-2 px-3 py-[2px]"
        sx={{
          "& svg": {
            color: colors.grey[900],
          },
          "& p": {
            color: colors.grey[900],
          },
          ".active & ": {
            "& svg": {
              color: colors.info,
            },
            "& p": {
              color: colors.info,
            },
          },
        }}
      >
        <Box
          className="flex flex-col rounded-md items-center gap-2 px-1.5 py-1.5 nav-item w-full"
          sx={{
            ".active & ": {
              backgroundColor: colors.card,
            },
          }}
        >
          {icon}
          <Typography className="nav-text">{title}</Typography>
        </Box>
      </Box>
  );

  if (newTab)
    return (
      <a href={to} target="_blank" rel="noreferrer" onClick={onSelect}>
        {content}
      </a>
    );

  return (
    <NavLink to={to} activeclassname="active" onClick={onSelect}>
      {content}
    </NavLink>
  );
};

export default MobileMenu;
